/**
 * The team, as every app sees it.
 *
 * One shape and one set of rules for "a member of this org", shared by My's
 * Users page, the Team panel every other app opens from its top bar, and
 * Cashier's till roster. They used to be three separate copies, and copies
 * drift: the till-PIN button reached My's table and never its phone layout,
 * so on a phone nobody could be given a PIN at all.
 */
import api from '../../lib/api'

export type TeamBranch = { id: number; name: string }

export type TeamMember = {
  id: number
  name: string
  email: string
  phone?: string
  role: string
  is_active: boolean
  branches: TeamBranch[]
  /** app_kinds this member is scoped to. Empty = no product-app access for a
   *  scoped role; owner/admin are never scoped. */
  app_scope?: string[]
  app_roles?: Record<string, string>
  /** Whether a till PIN is set. Never the PIN — the server only reports this. */
  has_pos_pin?: boolean
  /** A PIN-only cashier added by name, whose address is a placeholder. */
  placeholder_email?: boolean
  /** May give manual discounts at the till. Owners/admins always may. */
  pos_manual_discount?: boolean
}

export type RoleOption = { value: string; label: string; hint?: string; assignable?: boolean }

/** owner/admin are org-wide offices: every branch, every app, never scoped. */
export const ORG_WIDE_ROLES = new Set(['owner', 'admin'])

/** May give manual discounts at the till (mirrors services/manual_discount.py). */
export const canDiscount = (m: TeamMember) => ORG_WIDE_ROLES.has(m.role) || !!m.pos_manual_discount

/** Who can open the till: org-wide roles, and anyone scoped to Cashier.
 *  Mirrors services/entitlements.user_scoped_to_app(…, "pos"). */
export const canOpenTill = (m: TeamMember) =>
  ORG_WIDE_ROLES.has(m.role) || (m.app_scope ?? []).includes('pos')

export const teamApi = {
  list: () => api.get<TeamMember[]>('/users').then(r => r.data),
  update: (id: number, body: Partial<{
    role: string; is_active: boolean; branch_ids: number[]; app_scope: string[]
  }>) => api.patch<TeamMember>(`/users/${id}`, body).then(r => r.data),
  accessCatalog: () => api.get<{ roles: RoleOption[] }>('/users/access-catalog').then(r => r.data),
  branches: () => api.get<TeamBranch[]>('/branches').then(r => r.data),
  /** Allow or stop a person giving manual discounts at the till. */
  setDiscount: (id: number, allowed: boolean) =>
    api.put(`/pos/cashiers/${id}/discount`, { allowed }).then(r => r.data),
}

/** Where the full Users page lives (My), for everything the quick views
 *  leave out: branches, per-app access, roles, invites, removal. */
export const MY_USERS_URL =
  `${(((import.meta as any).env?.VITE_MY_URL as string) || '').replace(/\/$/, '')}/management/users`
